import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input, Button, Avatar } from 'antd';
import {
  ProfileLayout,
  UpcomingShowsSection,
  ShowPhotosSection,
  ShowCommentsSection,
  PeopleListSection,
  GenreTagsSection,
  AboutSection,
} from '../components/Profile';
import type { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import type { ShowListingProps } from '../components/DirectoryListings/ShowListing/ShowListing';
import type { CommentProps } from '../components/Comment/Comment';
import type { PhotoMetadata } from '../components/PhotoGallery/PhotoOverlayInfo';
import type { PersonItem } from '../components/Profile';
import { UserTag } from '../components/UserTag/UserTag';
import type { UserType } from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { DefaultAvatar, UserGroupIcon, ChatIcon, PlusCircleIcon } from '../components/icons';
import SpecModal from '../components/SpecModal/SpecModal';
import BandMembersDrawer from '../components/ManageDrawer/BandMembersDrawer';
import ShowContributorsDrawer from '../components/ManageDrawer/ShowContributorsDrawer';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { useShare } from '../hooks/useShare';
import './EditProfile.css';

const MOCK_BAND = {
  name: 'Johnny Mullenax Band',
  avatarUrl: '/images/bands/johnny-mullenax.png',
  heroImageUrl: '/images/bands/johnny-mullenax.png',
  about: 'Johnny Mullenax Band is aksjnsdkn sdkjfn sdkjndskjn sdkjnk jnsd sdkjfn sdkjndskjn sdkjnk jnsd sdkjfn sdkjndskjn sdkjnk jnsd value added value added · autoloremipsum',
  genres: ['Psychedelic', 'Bluegrass', 'Rock', 'Funk', 'Country'],
  websiteUrl: 'https://johnnymullenax.com',
  instagramUrl: 'https://instagram.com/johnnymullenaxband',
  soundcloudUrl: 'https://soundcloud.com/johnnymullenax/used-to-work-for-a-livin?si=59d3ed387ef142468a3e76b1f6fe26b7&utm_source=clipboard&utm_medium=text&utm_campaign=social_sharing',
};

const MOCK_SHOWS: Omit<ShowListingProps, 'onClick'>[] = [
  {
    bandName: 'Johnny Mullenax Band',
    date: 'Wed Sep 17',
    venue: 'Mercury Lounge',
    time: '12:00 PM',
    description: 'Live music, tasty brunch, mimosas, and good vibes! Enjoy a relaxing Sunday morning.',
    genres: ['Psychedelic', 'Bluegrass', 'Rock', 'Funk'],
    creator: 'Johnny Mullenax',
    creatorType: 'bandMember',
    imageUrl: '/images/bands/johnny-mullenax.png',
  },
  {
    bandName: 'Johnny Mullenax Band',
    date: 'Wed Sep 17',
    venue: 'The Colony',
    description: 'Live music, tasty brunch, mimosas, and good vibes!',
    genres: ['Psychedelic', 'Bluegrass', 'Rock', 'Funk'],
    creator: 'Johnny Mullenax',
    creatorType: 'bandMember',
    imageUrl: '/images/bands/johnny-mullenax.png',
  },
];

const MOCK_PHOTOS = [
  'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400',
  'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400',
  'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400',
  'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400',
  'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400',
  'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400',
  'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400',
  'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=400',
  'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400',
];

const MOCK_PHOTO_METADATA: PhotoMetadata[] = [
  { userName: 'Jane Smith', timestamp: 'Sept 16 3:29 PM', userTags: ['fan'], userProfileUrl: '/fan-profile', eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' } },
  { userName: 'George Smith', userAvatarUrl: 'https://i.pravatar.cc/150?img=8', timestamp: 'Sept 16 2:10 PM', userTags: ['fan', 'showContributor'], userProfileUrl: '/fan-profile', eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' } },
  { userName: 'Mike Johnson', userAvatarUrl: 'https://i.pravatar.cc/150?img=12', timestamp: 'Sept 15 8:45 PM', userTags: ['musician'], userProfileUrl: '/musician-profile', eventInfo: { artistName: 'The Restless Kind', date: 'Sat Sep 13', venue: 'The Vanguard' } },
  { userName: 'Sarah Wilson', timestamp: 'Sept 15 6:30 PM', userTags: ['fan'], userProfileUrl: '/fan-profile', eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Fri Sep 12', venue: "Cain's Ballroom" } },
  { userName: 'Tom Davis', userAvatarUrl: 'https://i.pravatar.cc/150?img=15', timestamp: 'Sept 14 2:15 PM', userTags: ['showContributor'], userProfileUrl: '/fan-profile', eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' } },
  { userName: 'Emily Clark', userAvatarUrl: 'https://i.pravatar.cc/150?img=20', timestamp: 'Sept 13 9:00 PM', userTags: ['fan'], userProfileUrl: '/fan-profile', eventInfo: { artistName: 'Neon Drifters', date: 'Sat Sep 6', venue: 'The Colony' } },
  { userName: 'Alex Turner', userAvatarUrl: 'https://i.pravatar.cc/150?img=22', timestamp: 'Sept 12 7:30 PM', userTags: ['musician'], userProfileUrl: '/musician-profile', eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 10', venue: 'Mercury Lounge' } },
  { userName: 'Chris Martin', timestamp: 'Sept 11 5:00 PM', userTags: [], eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' } },
  { userName: 'Lisa Ray', userAvatarUrl: 'https://i.pravatar.cc/150?img=25', timestamp: 'Sept 10 11:00 AM', userTags: ['fan'], userProfileUrl: '/fan-profile', eventInfo: { artistName: 'Johnny Mullenax Band', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Tue Sep 9', venue: 'Soundpony' } },
];

const MOCK_COMMENTS: Omit<CommentProps, 'className'>[] = [
  {
    userName: 'Jane Smith',
    timestamp: 'Sept 16 3:29 PM',
    userTags: ['fan', 'showContributor'],
    text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
    showEventInfo: true,
    eventInfo: { artistName: 'Johnny Mullen...', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
  },
  {
    userName: 'George Smith',
    avatarUrl: 'https://i.pravatar.cc/150?img=8',
    timestamp: 'Sept 16 2:10 PM',
    userTags: ['fan'],
    text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
    showEventInfo: true,
    eventInfo: { artistName: 'Johnny Mullen...', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
  },
];

const MOCK_MEMBERS: PersonItem[] = [
  { name: 'Johnny Mullenax', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38dada951f64025c659_johnny-mullenax(jmb).jpg', route: '/musician-profile' },
  { name: 'Greg Fallis', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38b9c4df6cfa82caa64_greg-fallis(jmb).jpg', route: '/musician-profile' },
  { name: 'Teddy Scott', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38f678f9f2223bab0fe_teddy-scott(jmb).jpg', route: '/musician-profile' },
  { name: 'Matt Magerkurth', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38f986cb72c84d1956b_matt-magerkurth(jmb).jpg', route: '/musician-profile' },
  { name: 'Andy McCormick', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38b2568f74884e3a456_andy-mccormick(jmb).jpg', route: '/musician-profile' },
  { name: 'Austin Stunkard', avatarUrl: 'https://cdn.prod.website-files.com/632688ceec38a92558da32b0/6670d38b30513cc84f419fcc_austin-stunkard(jmb).jpg', route: '/musician-profile' },
];

const MOCK_CONTRIBUTORS: PersonItem[] = [
  { name: 'Jane Smith', avatarUrl: 'https://i.pravatar.cc/150?img=5', route: '/fan-profile' },
];

const BandProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNewBand = !!(location.state as any)?.newBand;
  const isProfileSaved = (location.state as any)?.profileSaved === true;

  // TODO: conditionally show based on user's actual roles from API
  const [removeContributorModalOpen, setRemoveContributorModalOpen] = useState(false);
  const [removeContributorPasscode, setRemoveContributorPasscode] = useState('');

  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);

  // Management drawers
  const [bandMembersDrawerOpen, setBandMembersDrawerOpen] = useState(false);
  const [showContributorsDrawerOpen, setShowContributorsDrawerOpen] = useState(false);

  // Toast state
  const initialToast = isNewBand ? 'Band Profile Created' : isProfileSaved ? 'Profile Saved' : null;
  const [toastMessage, setToastMessage] = useState<string | null>(initialToast);
  const toastKeyRef = useRef(0);

  useEffect(() => {
    if (isNewBand || isProfileSaved) {
      window.history.replaceState({}, '');
    }
  }, [isNewBand, isProfileSaved]);

  const showToast = useCallback((message: string) => {
    toastKeyRef.current += 1;
    setToastMessage(message);
  }, []);

  const share = useShare(showToast);

  const actionItems: ActionSheetItem[] = [
    { key: 'share', label: 'Share', onClick: () => share({ title: MOCK_BAND.name }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => {
      navigator.clipboard?.writeText(window.location.href).catch(() => {});
      showToast('Link Copied');
    }},
    { key: 'edit-profile', label: 'Edit Profile', onClick: () => navigate('/edit-band-profile') },
  ];

  return (
    <ProfileLayout
      profileType="band"
      heroVariant="blurred"
      heroImageUrl={MOCK_BAND.heroImageUrl}
      showPlayButton
      soundcloudUrl={MOCK_BAND.soundcloudUrl}
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          {MOCK_BAND.avatarUrl ? (
            <img className="band-identity__avatar-img" src={MOCK_BAND.avatarUrl} alt={MOCK_BAND.name} />
          ) : (
            <div className="band-identity__avatar-placeholder">
              <DefaultAvatar size={48} />
            </div>
          )}
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{MOCK_BAND.name}</h1>
          <div className="band-identity__badges">
            <UserTag type="band" onClick={() => setOpenInfoType('band')} />
          </div>
        </div>
        <UserTypeInfo type="band" open={openInfoType === 'band'} onClose={() => setOpenInfoType(null)} />
        <div
          className="band-identity__members"
          onClick={() => document.getElementById('band-members-section')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <Avatar.Group size="default" max={{ count: 3, style: { backgroundColor: '#252B2D', color: '#fff', fontSize: 12 } }}>
            {MOCK_MEMBERS.map((member, index) => (
              <Avatar
                key={index}
                src={member.avatarUrl}
                style={!member.avatarUrl ? { backgroundColor: '#252B2D' } : undefined}
              />
            ))}
          </Avatar.Group>
          <span className="band-identity__members-text">
            With {MOCK_MEMBERS[0]?.name} and more...
          </span>
        </div>
      </div>
      <AboutSection text={MOCK_BAND.about} websiteUrl={MOCK_BAND.websiteUrl} instagramUrl={MOCK_BAND.instagramUrl} />

      <UpcomingShowsSection
        shows={MOCK_SHOWS}
        showAddButton
        addShowIcon={<ChatIcon size="s" tone="black" />}
        onAddShow={() => navigate('/add-show', { state: { bandId: 'jmb' } })}
      />
      <ShowPhotosSection photos={MOCK_PHOTOS} photoMetadata={MOCK_PHOTO_METADATA} subtitle="Photos from the band's past shows" />
      <ShowCommentsSection comments={MOCK_COMMENTS} showEventInfo />

      <PeopleListSection
        id="band-members-section"
        title="Band Members"
        people={MOCK_MEMBERS}
        actionLabel="Band Members"
        actionIcon={<UserGroupIcon size="s" tone="black" />}
        onAction={() => setBandMembersDrawerOpen(true)}
      />
      <PeopleListSection
        title="Show Contributor"
        people={MOCK_CONTRIBUTORS}
        actionLabel="Show Contributors"
        actionIcon={<PlusCircleIcon width={16} height={16} color="black" />}
        onAction={() => setShowContributorsDrawerOpen(true)}
      />

      <GenreTagsSection genres={MOCK_BAND.genres} />

      {/* TODO: conditionally show based on user's actual roles from API */}
      <button
        className="edit-profile-danger-link"
        onClick={() => setRemoveContributorModalOpen(true)}
      >
        Remove me as a Show Contributor
      </button>

      {/* Remove Show Contributor Modal */}
      <SpecModal
        open={removeContributorModalOpen}
        onCancel={() => setRemoveContributorModalOpen(false)}
        title="Remove Band"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type the "removemeasashowcontributor" to be removed from {MOCK_BAND.name}:
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Remove Passcode</span>
          <Input
            placeholder="Enter passcode to remove as contributor"
            value={removeContributorPasscode}
            onChange={(e) => setRemoveContributorPasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={removeContributorPasscode !== 'removemeasashowcontributor'}
            onClick={() => {
              console.log('Removed as show contributor');
              setRemoveContributorModalOpen(false);
              setRemoveContributorPasscode('');
            }}
          >
            Remove me as a Show Contributor
          </Button>
        </div>
      </SpecModal>

      {/* Management Drawers */}
      <BandMembersDrawer
        open={bandMembersDrawerOpen}
        onClose={() => setBandMembersDrawerOpen(false)}
        bandName={MOCK_BAND.name}
      />
      <ShowContributorsDrawer
        open={showContributorsDrawerOpen}
        onClose={() => setShowContributorsDrawerOpen(false)}
        bandName={MOCK_BAND.name}
      />

      <SpecAlert
        key={toastKeyRef.current}
        message={toastMessage}
        onDone={() => setToastMessage(null)}
      />
    </ProfileLayout>
  );
};

export default BandProfile;
